import React, { useState, useEffect } from 'react';
import { complianceApi, tenantApi, branchApi, formatApiErrorDetail, useAuth } from '../../lib/api';
import { formatDate, formatDateTime, downloadCSV, getAttachRateColor, getAttachRateBg } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertTriangle,
  Loader2,
  Download,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function CompliancePage() {
  const { user } = useAuth();
  const [exceptions, setExceptions] = useState([]);
  const [score, setScore] = useState(null);
  const [branchScores, setBranchScores] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenantFilter, setTenantFilter] = useState(user?.role === 'SUPER_ADMIN' ? 'all' : user?.tenant_id);
  const [branchFilter, setBranchFilter] = useState('all');

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = {};
        if (tenantFilter && tenantFilter !== 'all') params.tenant_id = tenantFilter;
        if (branchFilter && branchFilter !== 'all') params.branch_id = branchFilter;

        const [exceptionsRes] = await Promise.all([
          complianceApi.listExceptions(params),
        ]);
        setExceptions(exceptionsRes.data);

        if (isSuperAdmin) {
          const tenantsRes = await tenantApi.list({ contract_type: 'EXCLUSIVE', limit: 100 });
          setTenants(tenantsRes.data.items || tenantsRes.data);
        }

        const branchesRes = await branchApi.list();
        setBranches(branchesRes.data.items || branchesRes.data);

        const targetTenantId = tenantFilter !== 'all' ? tenantFilter : user?.tenant_id;
        if (targetTenantId) {
          const [scoreRes, branchScoresRes] = await Promise.all([
            complianceApi.getScore(targetTenantId),
            complianceApi.getBranchScores(targetTenantId),
          ]);
          setScore(scoreRes.data);
          setBranchScores(branchScoresRes.data);
        }
      } catch (error) {
        toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to load compliance data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tenantFilter, branchFilter, user, isSuperAdmin]);

  const handleExportCSV = () => {
    const exportData = exceptions.map(exc => ({
      Date: formatDateTime(exc.created_at),
      Branch: exc.branch_name,
      Staff: exc.staff_name,
      Device: exc.device_info,
      Reason: exc.override_reason,
    }));
    downloadCSV(exportData, `compliance-exceptions-${formatDate(new Date())}`);
    toast.success('Exceptions exported to CSV');
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Compliance
            </h1>
            <p className="text-slate-600 mt-1">Monitor warranty attachment compliance</p>
          </div>
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-button">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Compliance Score Card */}
        {score && (
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <ShieldCheck className="h-5 w-5 text-[#005DE8]" />
                Compliance Score - {format(new Date(), 'MMMM yyyy')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="col-span-2">
                  <div className="flex items-end gap-4 mb-3">
                    <p className={`text-5xl font-bold ${getAttachRateColor(score.attach_rate)}`} style={{ fontFamily: 'Outfit' }}>
                      {score.attach_rate}%
                    </p>
                    <p className="text-slate-500 pb-1">attach rate</p>
                  </div>
                  <Progress value={score.attach_rate} className="h-3" />
                  <div className="flex justify-between mt-2 text-sm text-slate-500">
                    <span>0%</span>
                    <span className="text-amber-600">70%</span>
                    <span className="text-emerald-600">90%</span>
                    <span>100%</span>
                  </div>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <p className="text-3xl font-bold text-slate-900">{score.total_sales}</p>
                  <p className="text-sm text-slate-500">total sales</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <p className="text-3xl font-bold text-emerald-600">{score.sales_with_warranty}</p>
                  <p className="text-sm text-slate-500">with warranty</p>
                </div>
              </div>
              {score.contract_type === 'EXCLUSIVE' && score.attach_rate < 80 && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">Below Target</p>
                    <p className="text-sm text-red-700">
                      Exclusive contract requires 80% minimum attach rate. Current: {score.attach_rate}%
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Per-Branch Score Cards */}
        {branchScores.length > 0 && (
          <Card className="border-slate-200" data-testid="branch-scores-card">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>
                Branch-wise Compliance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {branchScores.filter(b => b.total_sales > 0).map((branch) => (
                  <div key={branch.branch_id} className="p-4 rounded-xl border border-slate-200 hover:shadow-sm transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-medium text-slate-900 text-sm">{branch.branch_name}</p>
                      <span className={`text-lg font-bold ${getAttachRateColor(branch.attach_rate)}`}>
                        {branch.attach_rate}%
                      </span>
                    </div>
                    <div className="relative h-3 bg-slate-100 rounded-full overflow-hidden mb-2">
                      <div
                        className={`absolute left-0 top-0 h-full rounded-full transition-all ${getAttachRateBg(branch.attach_rate)}`}
                        style={{ width: `${Math.min(branch.attach_rate, 100)}%` }}
                      />
                      <div className="absolute left-[80%] top-0 w-0.5 h-full bg-slate-400 opacity-50" title="80% Target" />
                    </div>
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>{branch.sales_with_warranty}/{branch.total_sales} with warranty</span>
                      <span className="text-slate-400">Target: {branch.target}%</span>
                    </div>
                  </div>
                ))}
                {branchScores.filter(b => b.total_sales === 0).length > 0 && (
                  <div className="p-4 rounded-xl border border-dashed border-slate-200 text-center text-sm text-slate-400 flex items-center justify-center">
                    {branchScores.filter(b => b.total_sales === 0).length} branch(es) with no sales
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              {isSuperAdmin && (
                <Select value={tenantFilter} onValueChange={setTenantFilter}>
                  <SelectTrigger className="w-full sm:w-48" data-testid="tenant-filter">
                    <SelectValue placeholder="Tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tenants</SelectItem>
                    {tenants.map((tenant) => (
                      <SelectItem key={tenant.id} value={tenant.id}>
                        {tenant.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={branchFilter} onValueChange={setBranchFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="branch-filter">
                  <SelectValue placeholder="Branch" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.branch_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Exceptions Table */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Compliance Exceptions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {exceptions.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100">
                    <TableHead>Date</TableHead>
                    <TableHead>Branch</TableHead>
                    <TableHead>Staff</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead>Override Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {exceptions.map((exc) => (
                    <TableRow key={exc.id} className="table-row-hover">
                      <TableCell className="text-slate-600">
                        {formatDateTime(exc.created_at)}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {exc.branch_name}
                      </TableCell>
                      <TableCell className="font-medium text-slate-900">
                        {exc.staff_name}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {exc.device_info}
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <p className="text-sm text-slate-600 truncate" title={exc.override_reason}>
                          {exc.override_reason}
                        </p>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-16">
                <ShieldCheck className="h-12 w-12 text-emerald-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No exceptions</h3>
                <p className="text-slate-500 mt-1">All sales have warranties attached</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
